"use client";
import type { RowData } from "@tanstack/react-table";

import { lazyComponent } from "@/components/ui/lazy-component";
import { LoadFailureNotice } from "@/components/ui/load-failure-notice";
import { cn } from "@/lib/utils";
import { RecordPageShell } from "./record-page-shell";
import { RecordTableSkeleton } from "./record-table-skeleton";
import { useRecordPageFill } from "./record-scroll-boundary";
import { RecordTableBody } from "./record-table-body";
import { RecordTableDialogs } from "./record-table-dialogs";
import { useRecordTableDialogs } from "./use-record-table-dialogs";
import { useRecordTableModel } from "./use-record-table-model";
import type { RecordTableProps } from "./types";

const RecordTableHeader = lazyComponent(
  () =>
    import("./record-table-header").then((module) => module.RecordTableHeader),
  {
    loading: <div aria-hidden="true" className="h-11 shrink-0" />,
    failure: {
      label: "record-table-header",
      fallback: <LoadFailureNotice title="The table header could not load." />,
    },
  },
);

export function RecordTable<TData extends RowData>(
  props: RecordTableProps<TData>,
) {
  const { object, state, embedded = false, className } = props;
  const pageFill = useRecordPageFill(props.pageFill) && !embedded;
  const dialogs = useRecordTableDialogs();
  const model = useRecordTableModel(props);

  if (!state.isReady) {
    return (
      <RecordTableSkeleton
        label={object.label}
        pageFill={pageFill}
        className={className}
        showHeader={model.showHeader}
      />
    );
  }

  return (
    <RecordPageShell pageFill={pageFill} className={cn("gap-3", className)}>
      {model.showHeader ? (
        <RecordTableHeader
          object={object}
          state={state}
          views={model.allViews}
          activeViewId={model.activeViewId}
          canPersistViews={model.canPersistViews}
          actions={model.actions}
          title={props.title}
          primaryAction={props.primaryAction}
          headerActions={props.headerActions}
          objectSwitchOptions={props.objectSwitchOptions}
          onObjectSwitch={props.onObjectSwitch}
          onCreateView={dialogs.openSaveDialog}
          viewsInFilterSheet={model.showControls}
        />
      ) : null}
      <RecordTableBody
        props={props}
        model={model}
        onOpenColumns={() => dialogs.setColumnsOpen(true)}
        onSaveView={() => dialogs.openSaveDialog()}
      />
      <RecordTableDialogs props={props} model={model} dialogs={dialogs} />
    </RecordPageShell>
  );
}
